import { useCallback, useReducer, useEffect, useRef } from "react";
import { Descendant } from "slate";
import { SpeakerTypeEnum, ProgramTypeEnum } from "../../Enums/EventEnums";
import { IProgram, ISpeakerExtended } from "../../interfaces/models/IEvent";
import { timestampToDateString, timestampToTimeString } from "../../utils/dates";
import { getAllFirms } from "../../Services/Firm";
import { getCompanies } from "../../Services/Company";

export type PresenterProfessionalSource = "person" | "guestSpeaker";

export interface PresenterForm {
  personType: keyof typeof SpeakerTypeEnum;
  memberFirm: { id: string; name: string } | null;
  professional: { id: string; name: string } | null;
  professionalSource?: PresenterProfessionalSource;
}

export interface SessionFormState {
  requiresRegistration: boolean;
  registrationPageSessionTitle: string;
  sessionType: { id: string; name: string } | null;
  sessionTitle: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  timeFormat: "12" | "24";
  venue: string;
  location: string;
  sessionDescription: Descendant[] | undefined;
  presenters: PresenterForm[];
  postEventPresenters: PresenterForm[];
  addToPostEvent: boolean;
  postEventTitle: string;
  postEventDate: string;
  postEventType: { id: string; name: string } | null;
  postEventDescription: Descendant[] | undefined;
  takeaways: string[];
  youtubeUrl: string;
  sessionImagePreview: string | null;
  sessionImage: File | null;
  sessionImageUrl: string | null;
  documentType: string;
  sessionDocuments: File[];
  sessionDocumentUrls: string[];
}

const initialState: SessionFormState = {
  requiresRegistration: false,
  registrationPageSessionTitle: "",
  sessionType: null,
  sessionTitle: "",
  sessionDate: "",
  startTime: "",
  endTime: "",
  timeFormat: "12",
  venue: "",
  location: "",
  sessionDescription: undefined,
  presenters: [],
  postEventPresenters: [],
  addToPostEvent: false,
  postEventTitle: "",
  postEventDate: "",
  postEventType: null,
  postEventDescription: undefined,
  takeaways: ["", "", "", "", "", ""],
  youtubeUrl: "",
  sessionImagePreview: null,
  sessionImage: null,
  sessionImageUrl: null,
  documentType: "presentation",
  sessionDocuments: [],
  sessionDocumentUrls: [],
};

type SessionFormAction =
  | { type: "UPDATE_FIELD"; field: keyof SessionFormState; value: any }
  | { type: "RESET" }
  | { type: "INITIALIZE"; payload: Partial<SessionFormState> };

function sessionFormReducer(
  state: SessionFormState,
  action: SessionFormAction
): SessionFormState {
  switch (action.type) {
    case "UPDATE_FIELD":
      return {
        ...state,
        [action.field]: action.value,
      };
    case "RESET":
      return initialState;
    case "INITIALIZE":
      return {
        ...initialState,
        ...action.payload,
      };
    default:
      return state;
  }
}

export function useSessionForm(initialSession?: IProgram | null) {
  const [state, dispatch] = useReducer(sessionFormReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const updateSessionField = useCallback(
    <K extends keyof SessionFormState>(field: K, value: SessionFormState[K]) => {
      dispatch({ type: "UPDATE_FIELD", field, value });
    },
    []
  );

  const resetSessionForm = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const initializeSessionForm = useCallback((payload: Partial<SessionFormState>) => {
    dispatch({ type: "INITIALIZE", payload });
  }, []);

  const handleAddPresenter = useCallback(() => {
    const newPresenter: PresenterForm = {
      personType: "speaker" as keyof typeof SpeakerTypeEnum,
      memberFirm: null,
      professional: null,
    };
    updateSessionField("presenters", [...state.presenters, newPresenter]);
  }, [state.presenters, updateSessionField]);

  const handleUpdatePresenter = useCallback(
    (index: number, field: keyof PresenterForm, value: any) => {
      const currentPresenters = stateRef.current.presenters;
      const updatedPresenters = [...currentPresenters];
      if (index >= 0 && index < updatedPresenters.length) {
        updatedPresenters[index] = {
          ...updatedPresenters[index],
          [field]: value,
        };
        updateSessionField("presenters", updatedPresenters);
      }
    },
    [updateSessionField]
  );

  const handleRemovePresenter = useCallback(
    (index: number) => {
      const currentPresenters = stateRef.current.presenters;
      console.log("handleRemovePresenter called with index:", index);
      console.log("Current presenters:", currentPresenters);
      console.log("Presenters length:", currentPresenters.length);
      if (index < 0 || index >= currentPresenters.length) {
        console.error("Invalid index:", index, "for array length:", currentPresenters.length);
        return;
      }
      const filteredPresenters = currentPresenters.filter((_, i) => i !== index);
      console.log("Filtered presenters:", filteredPresenters);
      updateSessionField(
        "presenters",
        filteredPresenters
      );
    },
    [updateSessionField]
  );

  const handleUpdatePostEventPresenter = useCallback(
    (index: number, field: keyof PresenterForm, value: any) => {
      const currentPresenters = stateRef.current.postEventPresenters;
      const updatedPresenters = [...currentPresenters];
      if (index >= 0 && index < updatedPresenters.length) {
        updatedPresenters[index] = {
          ...updatedPresenters[index],
          [field]: value,
        };
        updateSessionField("postEventPresenters", updatedPresenters);
      }
    },
    [updateSessionField]
  );

  const handleRemovePostEventPresenter = useCallback(
    (index: number) => {
      const currentPresenters = stateRef.current.postEventPresenters;
      if (index < 0 || index >= currentPresenters.length) {
        return;
      }
      const filteredPresenters = currentPresenters.filter((_, i) => i !== index);
      updateSessionField("postEventPresenters", filteredPresenters);
    },
    [updateSessionField]
  );

  useEffect(() => {
    if (initialSession) {
      let sessionDescription: Descendant[] | undefined = undefined;
      if (initialSession.description) {
        try {
          const parsed = typeof initialSession.description === 'string' 
            ? JSON.parse(initialSession.description) 
            : initialSession.description;
          
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
            sessionDescription = parsed;
          } else {
            sessionDescription = [{
              type: "paragraph",
              children: [{ text: typeof initialSession.description === 'string' 
                ? initialSession.description 
                : JSON.stringify(initialSession.description) }],
            }];
          }
        } catch {
          sessionDescription = [{
            type: "paragraph",
            children: [{ text: String(initialSession.description) }],
          }];
        }
      }

      let postEventDescription: Descendant[] | undefined = undefined;
      if (initialSession.postEventDescription) {
        try {
          const parsed = typeof initialSession.postEventDescription === 'string' 
            ? JSON.parse(initialSession.postEventDescription) 
            : initialSession.postEventDescription;
          
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
            postEventDescription = parsed;
          } else {
            postEventDescription = [{
              type: "paragraph",
              children: [{ text: typeof initialSession.postEventDescription === 'string' 
                ? initialSession.postEventDescription 
                : JSON.stringify(initialSession.postEventDescription) }],
            }];
          }
        } catch {
          postEventDescription = [{
            type: "paragraph",
            children: [{ text: String(initialSession.postEventDescription) }],
          }];
        }
      }

      const formatPresentersAsync = async (): Promise<PresenterForm[]> => {
        if (!initialSession.speakers || initialSession.speakers.length === 0) {
          return [];
        }

        const [firms, companies] = await Promise.all([
          getAllFirms().catch(() => []),
          getCompanies().catch(() => []),
        ]);

        return initialSession.speakers.map((speaker) => {
          const speakerExtended = speaker as ISpeakerExtended;
          const isGuestSpeakerEntry =
            speakerExtended.personSource === "guestSpeaker" ||
            speaker.speakerType === "guest";

          let memberFirm: { id: string; name: string } | null = null;
          if (speakerExtended.firmName) {
            if (isGuestSpeakerEntry) {
              const company = companies.find(
                (c) =>
                  c.name?.toLowerCase() ===
                  speakerExtended.firmName?.toLowerCase(),
              );
              memberFirm = company
                ? { id: company.id, name: company.name }
                : { id: "", name: speakerExtended.firmName };
            } else {
              const firm = firms.find(
                (f) =>
                  f.name?.toLowerCase() ===
                    speakerExtended.firmName?.toLowerCase() ||
                  f.firmName?.toLowerCase() ===
                    speakerExtended.firmName?.toLowerCase(),
              );
              memberFirm = firm
                ? {
                    id: firm.id,
                    name: firm.name || firm.firmName || speakerExtended.firmName,
                  }
                : { id: "", name: speakerExtended.firmName };
            }
          }

          return {
            personType: speaker.speakerType,
            memberFirm,
            professional: speakerExtended.name
              ? { id: speaker.personId, name: speakerExtended.name }
              : { id: speaker.personId, name: "" },
            professionalSource: isGuestSpeakerEntry ? "guestSpeaker" : "person",
          };
        });
      };

      formatPresentersAsync().then((formattedPresenters) => {
        const postEventSpeakers = (initialSession as any).postEventSpeakers || initialSession.speakers || [];
        const formatPostEventPresentersAsync = async (): Promise<PresenterForm[]> => {
          if (!postEventSpeakers || postEventSpeakers.length === 0) {
            return formattedPresenters;
          }

          const [firms, companies] = await Promise.all([
            getAllFirms().catch(() => []),
            getCompanies().catch(() => []),
          ]);

          return postEventSpeakers.map((speaker: any) => {
            const speakerExtended = speaker as ISpeakerExtended;
            const isGuestSpeakerEntry =
              speakerExtended.personSource === "guestSpeaker" ||
              speaker.speakerType === "guest";

            let memberFirm: { id: string; name: string } | null = null;
            if (speakerExtended.firmName) {
              if (isGuestSpeakerEntry) {
                const company = companies.find(
                  (c) =>
                    c.name?.toLowerCase() ===
                    speakerExtended.firmName?.toLowerCase(),
                );
                memberFirm = company
                  ? { id: company.id, name: company.name }
                  : { id: "", name: speakerExtended.firmName };
              } else {
                const firm = firms.find(
                  (f) =>
                    f.name?.toLowerCase() ===
                      speakerExtended.firmName?.toLowerCase() ||
                    f.firmName?.toLowerCase() ===
                      speakerExtended.firmName?.toLowerCase(),
                );
                memberFirm = firm
                  ? {
                      id: firm.id,
                      name:
                        firm.name || firm.firmName || speakerExtended.firmName,
                    }
                  : { id: "", name: speakerExtended.firmName };
              }
            }

            return {
              personType: speaker.speakerType,
              memberFirm,
              professional: speakerExtended.name
                ? { id: speaker.personId, name: speakerExtended.name }
                : { id: speaker.personId, name: "" },
              professionalSource: isGuestSpeakerEntry ? "guestSpeaker" : "person",
            };
          });
        };

        formatPostEventPresentersAsync().then((formattedPostEventPresenters) => {
          const registrationPageSessionTitle = (initialSession as any).registrationPageSessionTitle || "";
          initializeSessionForm({
            requiresRegistration: initialSession.requiredRegistration || !!registrationPageSessionTitle,
            registrationPageSessionTitle: registrationPageSessionTitle,
            sessionType: initialSession.type
              ? { id: initialSession.type, name: ProgramTypeEnum[initialSession.type] }
              : null,
            sessionTitle: initialSession.title || "",
            sessionDate: initialSession.startTime ? timestampToDateString(initialSession.startTime) : "",
            startTime: initialSession.startTime ? timestampToTimeString(initialSession.startTime) : "",
            endTime: initialSession.endTime ? timestampToTimeString(initialSession.endTime) : "",
            venue: initialSession.venueName || "",
            location: initialSession.location || "",
            sessionDescription,
            presenters: formattedPresenters,
            postEventPresenters: formattedPostEventPresenters,
            addToPostEvent: initialSession.isPostEvent || false,
            postEventTitle: "",
            postEventDate: "",
            postEventType: null,
            postEventDescription,
            takeaways: initialSession.takeaways || ["", "", "", "", "", ""],
            youtubeUrl: initialSession.youtubeLink || "",
            sessionImagePreview: null,
            sessionImage: null,
            sessionImageUrl: initialSession.video || null,
            documentType: "presentation",
            sessionDocuments: [],
            sessionDocumentUrls: initialSession.documents && initialSession.documents.length > 0 ? initialSession.documents : [],
          });
        });
      });
    } else {
      resetSessionForm();
    }
  }, [initialSession, initializeSessionForm, resetSessionForm]);

  return {
    sessionForm: state,
    updateSessionField,
    resetSessionForm,
    initializeSessionForm,
    handleAddPresenter,
    handleUpdatePresenter,
    handleRemovePresenter,
    handleUpdatePostEventPresenter,
    handleRemovePostEventPresenter,
  };
}

